import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Config } from '../types/config.types';
import { preloadConfigImages, PreloadProgress } from '@utils/imagePreloader';

interface ConfigContextType {
  config: Config | null;
  loading: boolean;
  error: string | null;
  preloadProgress: PreloadProgress | null;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

interface ConfigProviderProps {
  children: ReactNode;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<Config | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preloadProgress, setPreloadProgress] = useState<PreloadProgress | null>(null);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        // Step 1: Load configuration
        // Use import.meta.env.BASE_URL to get the correct base path
        const configPath = `${import.meta.env.BASE_URL}config.json`;
        const response = await fetch(configPath);
        
        if (!response.ok) {
          throw new Error(`Failed to load configuration: ${response.statusText}`);
        }
        
        const data: Config = await response.json();
        
        // Validate required fields
        if (!data.theme || !data.business || !data.contact || !data.components) {
          throw new Error('Invalid configuration: missing required fields');
        }
        
        setConfig(data);
        applyTheme(data.theme);
        
        // Step 2: Preload images
        await preloadConfigImages(data, (progress) => {
          setPreloadProgress(progress);
        });
        
        // Step 3: Small delay to show completion
        await new Promise(resolve => setTimeout(resolve, 500));
        
        setLoading(false);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error loading configuration';
        setError(errorMessage);
        setLoading(false);
        console.error('Configuration loading error:', err);
      }
    };

    loadConfig();
  }, []);

  const applyTheme = (theme: Config['theme']) => {
    const root = document.documentElement;
    
    // Apply colors
    if (theme.colors) {
      if (theme.colors.primary) {
        root.style.setProperty('--color-primary', theme.colors.primary);
      }
      if (theme.colors.primaryDark) {
        root.style.setProperty('--color-primary-dark', theme.colors.primaryDark);
      }
      if (theme.colors.primaryLight) {
        root.style.setProperty('--color-primary-light', theme.colors.primaryLight);
      }
      if (theme.colors.secondary) {
        root.style.setProperty('--color-secondary', theme.colors.secondary);
      }
      if (theme.colors.accent) {
        root.style.setProperty('--color-accent', theme.colors.accent);
      }
    }
    
    // Apply fonts
    if (theme.fonts) {
      if (theme.fonts.primary) {
        root.style.setProperty('--font-primary', theme.fonts.primary);
      }
      if (theme.fonts.heading) {
        root.style.setProperty('--font-heading', theme.fonts.heading);
      }
    }
    
    // Load Google Fonts if specified
    if (theme.googleFonts && theme.googleFonts.length > 0) {
      loadGoogleFonts(theme.googleFonts);
    }
  };

  const loadGoogleFonts = (fonts: string[]) => {
    const fontString = fonts.join('&family=');
    const link = document.createElement('link');
    // Add font-display=swap for performance (eliminates invisible text flash)
    link.href = `https://fonts.googleapis.com/css2?family=${fontString}&display=swap`;
    link.rel = 'stylesheet';
    
    // Check if this font link already exists
    const existingLink = document.querySelector(`link[href="${link.href}"]`);
    if (!existingLink) {
      document.head.appendChild(link);
    }
  };

  const value: ConfigContextType = {
    config,
    loading,
    error,
    preloadProgress,
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
};